import { useState } from 'react';
import {
  CompanySector,
  CurrentUserDocument,
  useConsultingCompanyMaxProjectsCountProgressBarQuery,
  useRegisterNewProjectModalMutation,
} from '@/graphql/generated';
import { Loader } from '@/components/generic/Loader';
import { ModalConfig, useModal } from '@/components/modals/Modal';
import { useToast } from '@/components/layout/Toast';
import { MessageBox, MessageBoxType } from '@/components/layout/MessageBox';
import { useCurrentUser } from '@/providers/CurrentUserProvider';
import { useProjectContext } from '@/providers/ProjectContextProvider';
import { CompanySectorDropdown } from './project/CompanySectorDropdown';
import {
  ChooseProjectTemplateModal,
  chooseProjectTemplateModalConfig,
} from './project/ChooseProjectTemplate/ChooseProjectTemplateModal';
import { useTranslation } from '@/hooks/useTranslation';
import { LogoUploadForm } from '@/components/generic/LogoUploadForm';
import { NumberInput } from '@/components/generic/form/NumberEditor';

export const registerNewProjectModalConfig: ModalConfig = {
  title: undefined,
  isFullscreen: false,
  displayTitleBar: true,
};

export function RegisterNewProjectModal() {
  const { t } = useTranslation();
  const { currentUser } = useCurrentUser();
  const toast = useToast();
  const modal = useModal();
  const projectContext = useProjectContext();

  const [companyName, setCompanyName] = useState('');
  const [logo, setLogo] = useState<string | null | undefined>(null);
  const [sector, setSector] = useState<CompanySector | null | undefined>(
    undefined,
  );
  const [size, setSize] = useState<number | null | undefined>(undefined);
  const [annualTurnover, setAnnualTurnover] = useState('');

  const [createEnterprise] = useRegisterNewProjectModalMutation({
    refetchQueries: () => [CurrentUserDocument],
  });

  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const companyLicenseUsage =
    useConsultingCompanyMaxProjectsCountProgressBarQuery({
      variables: { companyId: currentUser?.company?.id || '' },
      skip: !currentUser?.company?.id,
      fetchPolicy: 'network-only',
    });

  const license = companyLicenseUsage.data?.consultingCompany.license;
  const maxProjectsCount = license?.licensesCount || null;
  const projectsCount =
    companyLicenseUsage.data?.consultingCompany.projectsCount || 0;
  const isUnlimited = maxProjectsCount === -1 || maxProjectsCount === null;
  const isMaxProjectsReached =
    (maxProjectsCount && projectsCount >= maxProjectsCount && !isUnlimited) ||
    false;

  const create = () => {
    setIsCreating(true);
    createEnterprise({
      variables: {
        input: {
          company: {
            name: companyName,
            logo,
            metadata: {
              sector,
              size,
              annualTurnover,
              customQuestionTypes:
                currentUser?.company?.metadata?.customQuestionTypes || [],
            },
          },
          ownedBy: {
            id: currentUser?.company?.id || '',
          },
        },
      },
    })
      .then((result) => {
        const createdEnterpriseId = result.data?.createEnterprise?.id;
        if (createdEnterpriseId) {
          toast.openToastWithMessage(t('toast:project.create.success'));
          projectContext?.updateEnterprise(createdEnterpriseId, true);
          modal.openModal(
            <ChooseProjectTemplateModal
              applyTemplateToCompanyId={createdEnterpriseId}
            />,
            {
              ...chooseProjectTemplateModalConfig,
            },
          );
        } else {
          setError(t('toast:project.create.error'));
        }
      })
      .catch((err) => {
        setError(err.message);
      })
      .finally(() => setIsCreating(false));
  };

  return (
    <div className="flex flex-col gap-4 max-w-lg">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <label htmlFor="companyName" className="form-input-label">
            {t('company.register_new_project_modal.form.company_name')}
          </label>
          <input
            type="text"
            id="companyName"
            className="form-input-text"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="logo" className="form-input-label">
            {t('company.register_new_project_modal.form.company_logo')}
          </label>

          <div className="flex gap-4 items-start">
            <div className="shrink-0 w-24 h-24 border border-gray-100 rounded-xl flex items-center justify-center p-2">
              {logo ? (
                <img src={logo} className="rounded-xl w-56" alt="logo" />
              ) : (
                <div className="italic sami-body-small text-gray-500">
                  {t('company.register_new_project_modal.form.no_logo')}
                </div>
              )}
            </div>

            <LogoUploadForm logo={logo} setLogo={setLogo} />
          </div>
        </div>

        <div className="flex gap-2">
          <div className="flex flex-col gap-2 w-1/2">
            <label htmlFor="sector" className="form-input-label">
              {t('company.register_new_project_modal.form.employees')}
            </label>
            <NumberInput value={size} setValue={setSize} />
          </div>
          <div className="flex flex-col gap-2 w-1/2">
            <label htmlFor="annualTurnOver" className="form-input-label">
              {t('company.register_new_project_modal.form.annual_turnover')}
            </label>
            <input
              type="text"
              name="annualTurnOver"
              id="annualTurnOver"
              className="form-input-text"
              placeholder={'250k€ - 5M€'}
              value={annualTurnover}
              onChange={(event) => setAnnualTurnover(event.target.value)}
            />
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="sector" className="form-input-label">
            {t('company.register_new_project_modal.form.business_sector')}
          </label>
          <CompanySectorDropdown sector={sector} setSector={setSector} />
        </div>
      </div>

      {error && <MessageBox type={MessageBoxType.Error}>{error}</MessageBox>}
      {isMaxProjectsReached && (
        <MessageBox type={MessageBoxType.Warning}>
          {t('company.register_new_project_modal.form.max_projects_reached')}
        </MessageBox>
      )}

      <div className="flex gap-2 justify-between pt-4">
        <button
          className="secondary"
          onClick={() => modal.closeModal()}
          disabled={isCreating}
        >
          {isCreating ? <Loader /> : t('global:cancel')}
        </button>
        <button
          className="primary purple"
          onClick={create}
          disabled={isCreating || !companyName || isMaxProjectsReached}
        >
          {isCreating ? <Loader /> : t('global:create')}
        </button>
      </div>
    </div>
  );
}
